import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import api from '../services/api';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale, LinearScale, PointElement, LineElement,
    Title, Tooltip, Legend, Filler,AnnotationController,
} from 'chart.js';
import {
    FaMoneyBillWave, FaChartLine, FaExclamationTriangle,
    FaPlusCircle, FaFileCsv, FaTrash, FaSpinner, FaRedo,
    FaThermometerEmpty, FaThermometerFull, FaThermometerHalf,
    FaInfoCircle, FaTimes, FaCheckCircle,
} from 'react-icons/fa';
import './FluxoCaixaProjetado.css';

ChartJS.register(
    CategoryScale, LinearScale, PointElement, LineElement,
    Title, Tooltip, Legend, Filler
);

/* ─── Formatadores ────────────────────────────────────────────── */
const fmtCur = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(typeof v === 'number' ? v : 0);

const fmtDate = (s) =>
    new Date(s).toLocaleDateString('pt-BR', { timeZone: 'UTC' });

const fmtShort = (s) =>
    new Date(s).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });

/* ─── Cenários ────────────────────────────────────────────────── */
const SCENARIOS = {
    pessimista: {
        id:          'pessimista',
        label:       'Pessimista',
        icon:        FaThermometerEmpty,
        color:       '#ef4444',
        colorAlpha:  'rgba(239,68,68,0.12)',
        receitas:    0.80,   // −20% nas receitas
        despesas:    1.15,   // +15% nas despesas
        desc:        '−20% receitas · +15% despesas',
    },
    realista: {
        id:          'realista',
        label:       'Realista',
        icon:        FaThermometerHalf,
        color:       '#3b82f6',
        colorAlpha:  'rgba(59,130,246,0.12)',
        receitas:    1.00,
        despesas:    1.00,
        desc:        'Dados atuais sem ajuste',
    },
    otimista: {
        id:          'otimista',
        label:       'Otimista',
        icon:        FaThermometerFull,
        color:       '#10b981',
        colorAlpha:  'rgba(16,185,129,0.12)',
        receitas:    1.20,   // +20% nas receitas
        despesas:    0.90,   // −10% nas despesas
        desc:        '+20% receitas · −10% despesas',
    },
};

const PERIODOS = [30, 60, 90];

/* ═══════════════════════════════════════════════════════════════
   HOOK: busca dados da API
═══════════════════════════════════════════════════════════════ */
const useProjecao = (dias) => {
    const [state, setState] = useState({ data: null, loading: true, error: null });

    const fetch_ = useCallback(async () => {
        setState(s => ({ ...s, loading: true, error: null }));
        try {
            const res = await api.get(`/api/fluxo-caixa/projetado?dias=${dias}`);
            setState({ data: res.data, loading: false, error: null });
        } catch (e) {
            setState({ data: null, loading: false, error: 'Falha ao buscar dados.' });
        }
    }, [dias]);

    useEffect(() => { fetch_(); }, [fetch_]);
    return { ...state, refetch: fetch_ };
};

/* ═══════════════════════════════════════════════════════════════
   HOOK: aplica cenário + simulações manuais sobre os dados base
═══════════════════════════════════════════════════════════════ */
const useProjecaoSimulada = (original, scenario, simulacoes) => {
    return useMemo(() => {
        if (!original?.dadosGrafico) return null;

        const sc = SCENARIOS[scenario];
        let acum = original.saldoInicial;
        const pontos = [];

        for (const dia of original.dadosGrafico) {
            // aplica multiplicadores do cenário
            let rec  = dia.receitas  * sc.receitas;
            let desp = dia.despesas  * sc.despesas;

            // aplica simulações manuais neste dia
            for (const sim of simulacoes) {
                if (sim.data === dia.data) {
                    if (sim.tipo === 'receita')  rec  += sim.valor;
                    else                          desp += sim.valor;
                }
            }

            acum += rec - desp;
            pontos.push({ ...dia, receitas: rec, despesas: desp, saldo: acum });
        }

        // KPIs
        const saldos = pontos.map(p => p.saldo);
        const iMin   = saldos.indexOf(Math.min(...saldos));

        return {
            saldoInicial: original.saldoInicial,
            dados:        pontos,
            kpis: {
                saldoFinal:       acum,
                saldoMinimo:      saldos[iMin] ?? 0,
                dataSaldoMinimo:  pontos[iMin] ? fmtDate(pontos[iMin].data) : '—',
                totalReceitas:    pontos.reduce((a, p) => a + p.receitas, 0),
                totalDespesas:    pontos.reduce((a, p) => a + p.despesas, 0),
                diasNegativos:    saldos.filter(s => s < 0).length,
            },
        };
    }, [original, scenario, simulacoes]);
};

/* ═══════════════════════════════════════════════════════════════
   HOOK: dados de todos os 3 cenários para comparação no gráfico
═══════════════════════════════════════════════════════════════ */
const useTodosCenarios = (original, simulacoes) => {
    return useMemo(() => {
        if (!original?.dadosGrafico) return {};
        const result = {};
        for (const [key, sc] of Object.entries(SCENARIOS)) {
            let acum = original.saldoInicial;
            result[key] = original.dadosGrafico.map(dia => {
                let rec  = dia.receitas * sc.receitas;
                let desp = dia.despesas * sc.despesas;
                for (const sim of simulacoes) {
                    if (sim.data === dia.data) {
                        if (sim.tipo === 'receita') rec  += sim.valor;
                        else                         desp += sim.valor;
                    }
                }
                acum += rec - desp;
                return acum;
            });
        }
        return result;
    }, [original, simulacoes]);
};

/* ═══════════════════════════════════════════════════════════════
   SUBCOMPONENTES
═══════════════════════════════════════════════════════════════ */

/* KPI Card */
const KpiCard = ({ icon: Icon, title, value, sub, tipo, loading }) => (
    <div className={`fcx-kpi ${tipo || ''} ${loading ? 'loading' : ''}`}>
        <div className="fcx-kpi-icon"><Icon /></div>
        <div className="fcx-kpi-body">
            <span className="fcx-kpi-title">{title}</span>
            {loading
                ? <div className="fcx-skeleton fcx-skeleton-val" />
                : <strong className="fcx-kpi-value">{value}</strong>}
            {sub && !loading && <span className="fcx-kpi-sub">{sub}</span>}
        </div>
    </div>
);

/* Botão de cenário */
const ScenarioBtn = ({ id, active, onClick }) => {
    const sc    = SCENARIOS[id];
    const Icon  = sc.icon;
    return (
        <button
            className={`fcx-scenario-btn ${id} ${active ? 'active' : ''}`}
            onClick={() => onClick(id)}
            title={sc.desc}
        >
            <Icon />
            <span>{sc.label}</span>
        </button>
    );
};

/* Modal de simulação */
const SimModal = ({ onClose, onSave, form, onChange }) => (
    <div className="fcx-modal-overlay" onClick={onClose}>
        <div className="fcx-modal" onClick={e => e.stopPropagation()}>
            <div className="fcx-modal-head">
                <h4>Nova Simulação</h4>
                <button onClick={onClose}><FaTimes /></button>
            </div>
            <div className="fcx-modal-body">
                <div className="fcx-field">
                    <label>Tipo</label>
                    <div className="fcx-toggle-row">
                        {['receita','despesa'].map(t => (
                            <button
                                key={t}
                                type="button"
                                className={`fcx-toggle-btn ${t} ${form.tipo === t ? 'active' : ''}`}
                                onClick={() => onChange({ target: { name: 'tipo', value: t } })}
                            >
                                {t === 'receita' ? 'Receita (+)' : 'Despesa (−)'}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="fcx-field">
                    <label>Descrição</label>
                    <input type="text" name="descricao" value={form.descricao} onChange={onChange} placeholder="Ex: Novo contrato, Manutenção..." />
                </div>
                <div className="fcx-field-row">
                    <div className="fcx-field">
                        <label>Valor (R$)</label>
                        <input type="number" name="valor" step="0.01" min="0" value={form.valor} onChange={onChange} placeholder="0,00" />
                    </div>
                    <div className="fcx-field">
                        <label>Data</label>
                        <input type="date" name="data" value={form.data} onChange={onChange} />
                    </div>
                </div>
            </div>
            <div className="fcx-modal-foot">
                <button className="fcx-btn-cancel" onClick={onClose}>Cancelar</button>
                <button className="fcx-btn-save"   onClick={onSave}>
                    <FaCheckCircle /> Adicionar
                </button>
            </div>
        </div>
    </div>
);

/* ═══════════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════════════════════ */
export default function FluxoCaixaProjetado() {
    const [dias,      setDias]     = useState(30);
    const [cenario,   setCenario]  = useState('realista');
    const [showAll,   setShowAll]  = useState(true);   // comparar todos os cenários no gráfico
    const [modalOpen, setModal]    = useState(false);
    const [simulacoes, setSims]    = useState([]);
    const [form, setForm] = useState({ tipo: 'despesa', descricao: '', valor: '', data: '' });

    const { data: original, loading, error, refetch } = useProjecao(dias);

    const projecao     = useProjecaoSimulada(original, cenario,    simulacoes);
    const todosCenarios = useTodosCenarios(original, simulacoes);

    /* CSV */
    const exportCSV = () => {
        if (!projecao) return;
        const rows = projecao.dados.map(d =>
            [fmtDate(d.data), d.receitas.toFixed(2), d.despesas.toFixed(2), d.saldo.toFixed(2)].join(',')
        );
        const blob = new Blob(['Data,Receitas,Despesas,Saldo\n' + rows.join('\n')], { type: 'text/csv' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `fluxo_caixa_${dias}d_${cenario}.csv`;
        a.click();
    };

    /* Simulação */
    const addSim = () => {
        if (!form.descricao || !form.valor || !form.data) return alert('Preencha todos os campos.');
        setSims(prev => [...prev, { ...form, valor: parseFloat(form.valor), id: Date.now() }]);
        setForm({ tipo: 'despesa', descricao: '', valor: '', data: '' });
        setModal(false);
    };

    /* Dados do gráfico */
    const chartData = useMemo(() => {
        if (!original?.dadosGrafico) return null;
        const labels = original.dadosGrafico.map(d => fmtShort(d.data));

        if (showAll) {
            // Exibe os 3 cenários como linhas comparativas
            return {
                labels,
                datasets: Object.entries(SCENARIOS).map(([key, sc]) => ({
                    label:           sc.label,
                    data:            todosCenarios[key] || [],
                    borderColor:     sc.color,
                    backgroundColor: sc.colorAlpha,
                    borderWidth:     key === cenario ? 2.5 : 1.5,
                    borderDash:      key === cenario ? [] : [5, 3],
                    pointRadius:     0,
                    pointHoverRadius: 5,
                    tension:         0.35,
                    fill:            key === cenario,
                })),
            };
        }

        // Modo cenário único com fill
        const sc = SCENARIOS[cenario];
        return {
            labels,
            datasets: [{
                label:           sc.label,
                data:            projecao?.dados.map(d => d.saldo) || [],
                borderColor:     sc.color,
                backgroundColor: sc.colorAlpha,
                borderWidth:     2.5,
                pointRadius:     0,
                pointHoverRadius: 6,
                tension:         0.35,
                fill:            true,
                segment: {
                    borderColor: ctx => ctx.p1.raw < 0 ? '#ef4444' : sc.color,
                    backgroundColor: ctx => ctx.p1.raw < 0 ? 'rgba(239,68,68,0.12)' : sc.colorAlpha,
                },
            }],
        };
    }, [original, todosCenarios, projecao, cenario, showAll]);

    const chartOptions = useMemo(() => ({
        responsive:          true,
        maintainAspectRatio: false,
        animation:           { duration: 600 },
        interaction:         { mode: 'index', intersect: false },
        plugins: {
            legend: {
                display:  showAll,
                position: 'top',
                labels:   { usePointStyle: true, pointStyle: 'circle', padding: 20, font: { size: 12 } },
            },
            tooltip: {
                backgroundColor: '#1e293b',
                titleColor:      '#94a3b8',
                bodyColor:       '#f1f5f9',
                borderColor:     'rgba(255,255,255,0.1)',
                borderWidth:     1,
                padding:         12,
                callbacks: {
                    label: ctx => ` ${ctx.dataset.label}: ${fmtCur(ctx.parsed.y)}`,
                },
            },
        },
        scales: {
            x: {
                grid:  { color: 'rgba(0,0,0,0.04)' },
                ticks: { maxTicksLimit: 10, font: { size: 11 }, color: '#94a3b8' },
            },
            y: {
                grid:  { color: 'rgba(0,0,0,0.04)' },
                ticks: { callback: v => fmtCur(v), font: { size: 11 }, color: '#94a3b8' },
            },
        },
        layout: { padding: { top: 10, right: 20, bottom: 10, left: 10 } },
    }), [showAll]);

    const kpis = projecao?.kpis;
    const sc   = SCENARIOS[cenario];

    /* ─── RENDER ─────────────────────────────────────────────── */
    return (
        <div className="fcx-root">

            {/* ── HEADER ── */}
            <div className="fcx-header">
                <div className="fcx-header-left">
                    <div className="fcx-header-icon"><FaChartLine /></div>
                    <div>
                        <h1>Fluxo de Caixa</h1>
                        <p>Projeção e simulação de cenários · {dias} dias à frente</p>
                    </div>
                </div>
                <div className="fcx-header-right">
                    {/* Seletor de período */}
                    <div className="fcx-period-tabs">
                        {PERIODOS.map(d => (
                            <button
                                key={d}
                                className={dias === d ? 'active' : ''}
                                onClick={() => setDias(d)}
                            >
                                {d}d
                            </button>
                        ))}
                    </div>
                    <button
                        className={`fcx-refresh-btn ${loading ? 'spin' : ''}`}
                        onClick={refetch}
                        disabled={loading}
                        title="Recarregar"
                    >
                        {loading ? <FaSpinner /> : <FaRedo />}
                    </button>
                </div>
            </div>

            {/* ── KPI STRIP ── */}
            <div className="fcx-kpi-row">
                <KpiCard
                    icon={FaMoneyBillWave}
                    title="Saldo Atual"
                    value={fmtCur(original?.saldoInicial)}
                    loading={loading}
                />
                <KpiCard
                    icon={sc.icon}
                    title={`Saldo Final (${sc.label})`}
                    value={fmtCur(kpis?.saldoFinal)}
                    tipo={kpis?.saldoFinal < 0 ? 'danger' : 'success'}
                    loading={loading}
                />
                <KpiCard
                    icon={FaExclamationTriangle}
                    title="Saldo Mínimo Projetado"
                    value={fmtCur(kpis?.saldoMinimo)}
                    sub={kpis?.dataSaldoMinimo ? `Previsto para ${kpis.dataSaldoMinimo}` : ''}
                    tipo={kpis?.saldoMinimo < 0 ? 'danger' : 'normal'}
                    loading={loading}
                />
                <KpiCard
                    icon={FaChartLine}
                    title="Dias com Saldo Negativo"
                    value={kpis?.diasNegativos ?? '—'}
                    sub={kpis?.diasNegativos > 0 ? 'Requer atenção' : 'Nenhum'}
                    tipo={kpis?.diasNegativos > 0 ? 'danger' : 'success'}
                    loading={loading}
                />
            </div>

            {/* ── CORPO: gráfico + painel ── */}
            <div className="fcx-body">

                {/* GRÁFICO */}
                <div className="fcx-chart-card">
                    <div className="fcx-chart-toolbar">
                        {/* Seletor de cenário ativo */}
                        <div className="fcx-scenarios">
                            {Object.keys(SCENARIOS).map(id => (
                                <ScenarioBtn
                                    key={id}
                                    id={id}
                                    active={cenario === id}
                                    onClick={setCenario}
                                />
                            ))}
                        </div>

                        {/* Toggle: cenário único vs comparação */}
                        <button
                            className={`fcx-compare-btn ${showAll ? 'active' : ''}`}
                            onClick={() => setShowAll(v => !v)}
                            title="Comparar todos os cenários"
                        >
                            {showAll ? 'Comparando 3 cenários' : 'Comparar cenários'}
                        </button>
                    </div>

                    <div className="fcx-chart-wrap">
                        {loading && (
                            <div className="fcx-chart-overlay">
                                <FaSpinner className="fcx-spin" />
                                <span>Calculando projeção...</span>
                            </div>
                        )}
                        {error && (
                            <div className="fcx-chart-overlay error">
                                <FaExclamationTriangle />
                                <span>{error}</span>
                            </div>
                        )}
                        {!loading && !error && chartData && (
                            <Line data={chartData} options={chartOptions} />
                        )}
                    </div>

                    {/* Linha zero (visual hint) */}
                    {!showAll && kpis?.diasNegativos > 0 && (
                        <div className="fcx-zero-hint">
                            <FaExclamationTriangle />
                            {kpis.diasNegativos} dia{kpis.diasNegativos > 1 ? 's' : ''} com saldo negativo no cenário {sc.label.toLowerCase()}
                        </div>
                    )}
                </div>

                {/* PAINEL LATERAL */}
                <div className="fcx-side">

                    {/* Info do cenário ativo */}
                    <div className="fcx-scenario-info">
                        <div className={`fcx-scenario-badge ${cenario}`}>
                            <sc.icon />
                            <div>
                                <strong>{sc.label}</strong>
                                <span>{sc.desc}</span>
                            </div>
                        </div>
                        {kpis && (
                            <div className="fcx-scenario-numbers">
                                <div>
                                    <span>Receitas projetadas</span>
                                    <strong className="pos">{fmtCur(kpis.totalReceitas)}</strong>
                                </div>
                                <div>
                                    <span>Despesas projetadas</span>
                                    <strong className="neg">{fmtCur(kpis.totalDespesas)}</strong>
                                </div>
                                <div>
                                    <span>Resultado líquido</span>
                                    <strong className={kpis.totalReceitas - kpis.totalDespesas >= 0 ? 'pos' : 'neg'}>
                                        {fmtCur(kpis.totalReceitas - kpis.totalDespesas)}
                                    </strong>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Simulações manuais */}
                    <div className="fcx-sim-panel">
                        <div className="fcx-sim-header">
                            <h4>Simulações manuais</h4>
                            <span className="fcx-sim-hint">
                                <FaInfoCircle /> Afeta todos os cenários
                            </span>
                        </div>

                        {simulacoes.length === 0 ? (
                            <p className="fcx-sim-empty">Nenhuma simulação adicionada.</p>
                        ) : (
                            <ul className="fcx-sim-list">
                                {simulacoes.map(s => (
                                    <li key={s.id} className={`fcx-sim-item ${s.tipo}`}>
                                        <div>
                                            <span>{s.descricao}</span>
                                            <small>{fmtCur(s.valor)} · {fmtDate(s.data)}</small>
                                        </div>
                                        <button onClick={() => setSims(prev => prev.filter(x => x.id !== s.id))}>
                                            <FaTrash />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <button className="fcx-btn-add-sim" onClick={() => setModal(true)}>
                            <FaPlusCircle /> Adicionar simulação
                        </button>
                    </div>

                    {/* Exportar CSV */}
                    <button className="fcx-btn-csv" onClick={exportCSV} disabled={!projecao}>
                        <FaFileCsv /> Exportar CSV ({sc.label})
                    </button>
                </div>
            </div>

            {/* MODAL */}
            {modalOpen && (
                <SimModal
                    onClose={() => setModal(false)}
                    onSave={addSim}
                    form={form}
                    onChange={e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                />
            )}
        </div>
    );
}